import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db/db';
import { useAthleteStore } from '@/store';
import { useMemo } from 'react';

export interface WrestlerStats {
    ovr: number;
    subRatings: {
        offense: number;
        defense: number;
        conditioning: number;
        matIQ: number;
        topGame: number;
        bottomGame: number;
    };
    archetype: string;
    level: number;
    xp: number;
    nextLevelXp: number;
    avatar?: string;
}

export function useWrestlerStats() {
    const activeId = useAthleteStore((s) => s.activeId);

    const activeAthlete = useAthleteStore((s) => s.athletes.find(a => a.id === s.activeId));

    // 1. Fetch Raw Data
    const sessions = useLiveQuery(() =>
        db.trainingSessions.where('athleteId').equals(activeId).toArray()
        , [activeId]) ?? [];

    const skillProgress = useLiveQuery(() =>
        db.skillProgress.where('athleteId').equals(activeId).toArray()
        , [activeId]) ?? [];

    const allSkills = useLiveQuery(() => db.skills.toArray()) ?? [];

    // 2. Calculation Logic
    const stats: WrestlerStats = useMemo(() => {
        // A. Basic Counts
        // const totalSessions = sessions.length; // Unused
        const totalMinutes = sessions.reduce((sum, s) => sum + s.totalSeconds, 0) / 60;

        const masteredSkills = skillProgress.filter(p => p.status === 'mastered').length;
        const workedSkills = skillProgress.filter(p => p.status === 'worked').length;
        // const totalSkillsAvailable = allSkills.length || 1; // Unused

        // B. Calculate Sub-Ratings (0-99 scale)

        // Conditioning: Based on total minutes and consistency
        // Base 40 + 1 per 30 mins trained (capped at 99)
        let conditioning = Math.min(99, 40 + Math.floor(totalMinutes / 30));

        // Mat IQ: Based on number of different skills worked/mastered
        // Base 40 + 2 per skill worked
        let matIQ = Math.min(99, 40 + (workedSkills * 2) + (masteredSkills * 5));

        // Offense: Specific to Neutral/Takedown skills
        // For now, heuristic based on masteries + session bias
        const offenseSkills = allSkills.filter(s => s.position === 'neutral');
        const offenseMastered = skillProgress.filter(p =>
            p.status === 'mastered' && offenseSkills.some(s => s.id === p.skillId)
        ).length;
        let offense = Math.min(99, 45 + (offenseMastered * 8));

        // Defense: Base + specific defense skills
        const defenseSkills = allSkills.filter(s => s.pillarId === 'p-defense');
        const defenseMastered = skillProgress.filter(p =>
            p.status === 'mastered' && defenseSkills.some(s => s.id === p.skillId)
        ).length;
        let defense = Math.min(99, 45 + (defenseMastered * 10));

        // Top/Bottom
        const topSessions = sessions.filter(s => s.position === 'top').length;
        let topGame = Math.min(99, 40 + (topSessions * 2));

        const bottomSessions = sessions.filter(s => s.position === 'bottom').length;
        let bottomGame = Math.min(99, 40 + (bottomSessions * 2));


        // C. Archetype Determination
        // Simple logic: highest rating determines tag
        const ratings = {
            'Takedown Artist': offense,
            'Iron Wall': defense,
            'Mat Rat': topGame,
            'Escape Artist': bottomGame,
            'Conditioning Beast': conditioning,
            'Technician': matIQ,
        };

        const archetype = Object.entries(ratings).reduce((a, b) => a[1] > b[1] ? a : b)[0];

        // D. Calculate OVR (Overall) - Weighted Average
        // Weight IQ and Conditioning slightly less than pure technique
        const weightedSum = (offense * 1.2) + (defense * 1.2) + (topGame) + (bottomGame) + (conditioning * 0.8) + (matIQ * 0.8);
        const ovr = Math.min(99, Math.round(weightedSum / 6));

        // Level / XP System (Mock for now)
        const xp = Math.floor(totalMinutes * 10) + (masteredSkills * 100);
        const level = Math.floor(Math.sqrt(xp / 100)) + 1; // Simple quadratic curve
        const nextLevelXp = Math.pow(level, 2) * 100 + 100;

        return {
            ovr,
            subRatings: { offense, defense, conditioning, matIQ, topGame, bottomGame },
            archetype,
            level,
            xp,
            nextLevelXp,
            avatar: activeAthlete?.avatar // Return avatar
        };

    }, [sessions, skillProgress, allSkills, activeAthlete]);

    return stats;
}
